import type { QuoteItemTranslation } from '@/types'

/**
 * A QuoteItem `name` / `description` as it arrives from the API.
 *
 * Modern rows carry a {@link QuoteItemTranslation} map, but LEGACY rows (and
 * some hand-seeded/imported quotes) still carry a plain string. Both shapes
 * must be tolerated on read — hence the union, and hence this helper.
 */
export type QuoteTranslatableValue =
  | QuoteItemTranslation
  | string
  | null
  | undefined

/**
 * Resolve a QuoteItem name/description (translation map OR plain string) to a
 * displayable string, so item.name never renders as "[object Object]".
 *
 * Uses the quote language, then falls back en -> it -> al -> any string.
 *
 * The plain-string branch is deliberate legacy defense: pre-i18n quote items
 * persisted `name`/`description` as scalars. Do not "simplify" it away.
 */
export function resolveQuoteTranslation(
  value: QuoteTranslatableValue,
  language: string = 'en',
): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  return (
    value[language as keyof QuoteItemTranslation] ??
    value.en ??
    value.it ??
    value.al ??
    Object.values(value).find((v) => typeof v === 'string') ??
    ''
  )
}
